import { addYears, differenceInYears, getYear, parseISO } from 'date-fns'
import { batch } from 'react-redux'
import { customizeError } from '@chilecompra/react-kit/errors'
import { formatDNI } from '@chilecompra/react-kit/utils'
import {
  isDevEnv,
  isLocalEnv,
  REACT_APP_FEATURE_FLAG_BUYER_ACTIVITIES_ENABLED,
  REACT_APP_FEATURE_FLAG_RECAPTCHA_ENABLED
} from '../../config/settings/environment'
import { makeActionCreator } from '../../config/store/utils'
import { getBusinessIncomeBrackets, getDistricts, getStates } from '../../services/config'
import {
  getBuyerAlreadyRegistered,
  getBuyerPendingRequests,
  getOrganismTypes,
  getDirectors,
  getSurrogants,
  sendBuyerRegistrationData,
  getBuyerRegistrationPDF,
  sendBuyerRegistrationEmail,
  getBuyerAgreementPDF
} from '../../services/buyers'
import { getIdentity, recoverAdmin } from '../../services/register'
import { DNI_TEST, IDENTIFICATION_TYPE } from '../../config/settings/constants'
import { initiateFlow, registerFlow } from '../../services/flow'
import { registerError } from '../../services/errors'
import { sendValidationEmail, codeValidation } from '../../services/mailing'
import { onLogoutUniquePassThunk } from '../UniquePassAuth/UniquePassAuth.actions'
import { createNameComplete, getPersonsFromServiceData } from '../../modules/utils/parsers'
import { renameProperties } from '../../modules/utils/helpers'
import {
  getBuyerActivities,
  getBuyerAddress,
  getBuyerBusiness,
  getBuyerIncomeBracket,
  getBuyerIncomeSales,
  getRepresentatives
} from '../../services/sii'
import { getFileFromBucket } from '../../services/attachments'

export const GET_GUEST_FULL_NAME = 'GET_GUEST_FULL_NAME'
export const GET_GUEST_FULL_NAME_ERROR = 'GET_GUEST_FULL_NAME_ERROR'
export const GET_GUEST_FULL_NAME_SUCCESS = 'GET_GUEST_FULL_NAME_SUCCESS'
export const onGetGuestFullName = makeActionCreator(GET_GUEST_FULL_NAME)
export const onGetGuestFullNameError = makeActionCreator(GET_GUEST_FULL_NAME_ERROR, 'payload')
export const onGetGuestFullNameSuccess = makeActionCreator(GET_GUEST_FULL_NAME_SUCCESS, 'payload')
export const onGetGuestFullNameThunk = payload => async dispatch => {
  dispatch(onGetGuestFullName())

  try {
    if (isDevEnv && DNI_TEST.includes(payload.dni)) {
      return batch(() => {
        dispatch(
          onGetGuestFullNameSuccess({
            dni: payload.dni,
            firstLastName: 'Del Rio',
            gender: 2,
            name: 'Maria Carmen de los Angeles',
            secondLastName: 'Gonzalez'
          })
        )
        payload.callback()
      })
    }
    const { data } = await getIdentity({ dni: formatDNI(payload.dni, 2) })

    return batch(() => {
      dispatch(
        onGetGuestFullNameSuccess({
          dni: payload.dni,
          firstLastName: data.payload?.apellidoPaterno,
          gender: data.payload?.sexo === 'F' ? 2 : 1,
          name: data.payload?.nombres,
          secondLastName: data.payload?.apellidoMaterno || ''
        })
      )
      payload.callback()
    })
  } catch (error) {
    if (error.originalError?.response?.data?.errores[0]?.codigo === '400-6') {
      return dispatch(onGetGuestFullNameError(customizeError({ code: 400, reason: 'DNI_BELONGS_TO_A_DECEASED_ERROR' })))
    }

    return dispatch(onGetGuestFullNameError(error.toObject()))
  }
}

export const SAVE_TEMPORARY_PERSONAL_INFORMATION = 'SAVE_TEMPORARY_PERSONAL_INFORMATION'
export const onSaveTemporaryPersonalInformation = makeActionCreator(SAVE_TEMPORARY_PERSONAL_INFORMATION, 'payload')

export const SAVE_TEMPORARY_AUTHORITY_INFORMATION = 'SAVE_TEMPORARY_AUTHORITY_INFORMATION'
export const onSaveTemporaryAuthorityInformation = makeActionCreator(SAVE_TEMPORARY_AUTHORITY_INFORMATION, 'payload')

export const SAVE_TEMPORARY_LEGAL_REPRESENTATION = 'SAVE_TEMPORARY_PERSONERIA_INFORMATION'
export const onSaveTemporaryLegalRepresentation = makeActionCreator(SAVE_TEMPORARY_LEGAL_REPRESENTATION, 'payload')

export const RECOVER_ADMIN = 'RECOVER_ADMIN'
export const RECOVER_ADMIN_ERROR = 'RECOVER_ADMIN_ERROR'
export const RECOVER_ADMIN_SUCCESS = 'RECOVER_ADMIN_SUCCESS'
export const onRecoverAdmin = makeActionCreator(RECOVER_ADMIN)
export const onRecoverAdminError = makeActionCreator(RECOVER_ADMIN_ERROR, 'payload')
export const onRecoverAdminSuccess = makeActionCreator(RECOVER_ADMIN_SUCCESS)
export const onRecoverAdminThunk =
  ({ executeRecaptcha = () => undefined, onSuccessCallback = undefined } = {}) =>
  async (dispatch, getState) => {
    dispatch(onRecoverAdmin())
    try {
      const state = getState().registerWizard
      let token = '1234'
      if (REACT_APP_FEATURE_FLAG_RECAPTCHA_ENABLED === true || REACT_APP_FEATURE_FLAG_RECAPTCHA_ENABLED === 'true') {
        token = await executeRecaptcha()
      }

      await recoverAdmin({
        token,
        institucion: renameProperties(state.business, {
          name: 'razonSocial',
          dni: 'rut'
        }),
        flow: { idFlujo: state.flow.data.flowId },
        guest: renameProperties(state.guest, {
          isUniquePass: 'esClaveUnica',
          name: 'nombres',
          lastName: 'apellidos',
          dni: 'rut',
          gender: 'genero',
          phone: 'telefono',
          position: 'cargo',
          email: 'correo'
        })
      })
      return batch(() => {
        dispatch(onRecoverAdminSuccess())
        onSuccessCallback()
      })
    } catch (error) {
      return dispatch(onRecoverAdminError(customizeError({ code: 500, reason: 'SOMETHING_WENT_WRONG_ERROR' })))
    }
  }

export const REGISTER_CODE_VALIDATION = ' REGISTER_CODE_VALIDATION'
export const REGISTER_CODE_VALIDATION_ERROR = ' REGISTER_CODE_VALIDATION_ERROR'
export const REGISTER_CODE_VALIDATION_SUCCESS = ' REGISTER_CODE_VALIDATION_SUCCESS'
export const onRegisterCodeValidation = makeActionCreator(REGISTER_CODE_VALIDATION)
export const onRegisterCodeValidationError = makeActionCreator(REGISTER_CODE_VALIDATION_ERROR, 'payload')
export const onRegisterCodeValidationSuccess = makeActionCreator(REGISTER_CODE_VALIDATION_SUCCESS, 'payload')
export const onRegisterCodeValidationThunk =
  ({ code, email }) =>
  async (dispatch, getState) => {
    dispatch(onRegisterCodeValidation())

    try {
      const { id } = getState().registerWizard.guest
      const flowId = getState().registerWizard.flow?.data?.flowId

      const { data } = await codeValidation({ code, email, flowId, id })
      const isValid = data.payload

      if (isValid) {
        return dispatch(onRegisterCodeValidationSuccess({ code }))
      } else {
        return dispatch(onRegisterCodeValidationError())
      }
    } catch (error) {
      return dispatch(onRegisterCodeValidationError({ error }))
    }
  }

export const RESET_REGISTER_WIZARD_STATE = 'RESET_REGISTER_WIZARD_STATE'
export const onResetRegisterWizardState = makeActionCreator(RESET_REGISTER_WIZARD_STATE)

export const SET_REGISTER_TYPE = 'SET_REGISTER_TYPE'
export const onRegisterBy = makeActionCreator(SET_REGISTER_TYPE, 'payload')

export const CLEAN_GUEST_BUSINESS_ERRORS_SUCCESS = 'CLEAN_GUEST_BUSINESS_ERRORS_SUCCESS'
export const onCleanGuestBusinessErrorsSuccess = makeActionCreator(CLEAN_GUEST_BUSINESS_ERRORS_SUCCESS)

export const CHECK_GUEST_BUSINESS = 'CHECK_GUEST_BUSINESS'
export const CHECK_GUEST_BUSINESS_ERROR = 'CHECK_GUEST_BUSINESS_ERROR'
export const CHECK_GUEST_BUSINESS_SUCCESS = 'CHECK_GUEST_BUSINESS_SUCCESS'
export const CHECK_GUEST_BUSINESS_EXIST = 'CHECK_GUEST_BUSINESS_EXIST'
export const onCheckGuestBusiness = makeActionCreator(CHECK_GUEST_BUSINESS, 'payload')
export const onCheckGuestBusinessError = makeActionCreator(CHECK_GUEST_BUSINESS_ERROR, 'payload')
export const onCheckGuestBusinessSuccess = makeActionCreator(CHECK_GUEST_BUSINESS_SUCCESS, 'payload')
export const onCheckGuestBusinessExist = makeActionCreator(CHECK_GUEST_BUSINESS_EXIST, 'payload')
export const onCheckGuestBusinessThunk = payload => async dispatch => {
  dispatch(onCheckGuestBusiness({ dni: payload.dni }))

  try {
    const formattedDNI = formatDNI(payload.dni, 2)
    const { data: businessData } = await getBuyerBusiness({ dni: formattedDNI }).catch(() => {
      throw customizeError({ code: 400, reason: 'SOMETHING_WENT_WRONG_ERROR' })
    })
    const { apellidoMaterno, apellidoPaterno, nombre, razonSocial: orgLegalName } = businessData.payload || {}

    const entityType = businessData?.payload?.tipoContribuyenteCod === 1 ? 'natural' : 'legal'

    const isPublicOrg =
      businessData.payload?.tipoContribuyenteCod === 5 || businessData.payload?.tipoContribuyenteCod === 6 ? 1 : 0

    if (entityType === 'natural') {
      throw customizeError({ code: 400, reason: 'DNI_NOT_ALLOWED_ERROR' })
    }

    const { data: buyerDataPendingRequest } = await getBuyerPendingRequests({ dni: payload.dni }).catch(() => {
      throw customizeError({ code: 400, reason: 'SOMETHING_WENT_WRONG_ERROR' })
    })

    const { solicitudId: idRequest } = buyerDataPendingRequest?.payload || {}

    if (buyerDataPendingRequest.payload?.estaPendiente) {
      const error = customizeError({ code: 400, reason: 'PENDING_REQUEST' })
      const extendedError = {
        ...error,
        idRequest,
        orgLegalName
      }
      throw extendedError
    }

    const { data: buyerDataAlreadyRegistered } = await getBuyerAlreadyRegistered({ dni: payload.dni })

    if (buyerDataAlreadyRegistered?.payload?.existe === true) {
      dispatch(
        onCheckGuestBusinessExist({
          entityType,
          isPublicOrg,
          name: entityType === 'legal' ? orgLegalName : `${nombre} ${apellidoPaterno} ${apellidoMaterno}`,
          dni: payload.dni
        })
      )
      throw customizeError({
        code: 400,
        reason: 'DNI_ALREADY_REGISTERED'
      })
    }

    if (
      REACT_APP_FEATURE_FLAG_BUYER_ACTIVITIES_ENABLED === true ||
      REACT_APP_FEATURE_FLAG_BUYER_ACTIVITIES_ENABLED === 'true'
    ) {
      const { data: buyerActivitiesData } = await getBuyerActivities({ dni: payload.dni }).catch(() => {
        throw customizeError({ code: 400, reason: 'SOMETHING_WENT_WRONG_ERROR' })
      })

      if (!buyerActivitiesData.payload?.estado) {
        throw customizeError({ code: 400, reason: 'DNI_NOT_INITIALIZED_ERROR' })
      }
    }

    return batch(() => {
      dispatch(
        onCheckGuestBusinessSuccess({
          entityType,
          isPublicOrg,
          name: entityType === 'legal' ? orgLegalName : `${nombre} ${apellidoPaterno} ${apellidoMaterno}`,
          dni: payload.dni
        })
      )

      if (entityType === 'legal') {
        return
      }

      payload.callback()
    })
  } catch (error) {
    const errorObject = error.toObject && typeof error.toObject === 'function' ? error.toObject() : error
    return batch(() => {
      const flow = 'INICIO_PASO_3'
      dispatch(onCheckGuestBusinessError(errorObject))
      dispatch(
        onRegisterErrorThunk({
          origin: flow,
          message: errorObject.reason
        })
      )
    })
  }
}

export const GET_GUEST_BUSINESS_OPTIONS = 'GET_GUEST_BUSINESS_OPTIONS'
export const GET_GUEST_BUSINESS_OPTIONS_ERROR = 'GET_GUEST_BUSINESS_OPTIONS_ERROR'
export const GET_GUEST_BUSINESS_OPTIONS_SUCCESS = 'GET_GUEST_BUSINESS_OPTIONS_SUCCESS'
export const onGetGuestBusinessOptions = makeActionCreator(GET_GUEST_BUSINESS_OPTIONS)
export const onGetGuestBusinessOptionsError = makeActionCreator(GET_GUEST_BUSINESS_OPTIONS_ERROR, 'payload')
export const onGetGuestBusinessOptionsSuccess = makeActionCreator(GET_GUEST_BUSINESS_OPTIONS_SUCCESS, 'payload')
export const onGetGuestBusinessOptionsThunk = () => async (dispatch, getState) => {
  dispatch(onGetGuestBusinessOptions())

  try {
    const { business } = getState().registerWizard
    const initialYear = getYear(addYears(new Date(), -1))
    const maxYear = getYear(addYears(new Date(), -2))
    const formattedDNI = formatDNI(business.dni, 2)
    const getBusinessIncome = business.entityType === 'legal' ? getBuyerIncomeSales : getBuyerIncomeBracket
    const getRecursiveBusinessIncome = (dni, year) =>
      getBusinessIncome({ dni, year })
        .then(response => [response, year])
        .catch(error => {
          if (year > maxYear) {
            return getRecursiveBusinessIncome(dni, year - 1)
          }

          throw error
        })
    const [
      { data: businessAddressData },
      { data: businessIncomeBracketsData },
      [{ data: businessIncomeData }, businessIncomeYear],
      { data: businessStatesData }
    ] = await Promise.all([
      getBuyerAddress({ dni: formattedDNI }).catch(() => ({ data: {} })),
      getBusinessIncomeBrackets().catch(() => ({ data: {} })),
      getRecursiveBusinessIncome(formattedDNI, initialYear).catch(() => [{ data: {} }, initialYear]),
      getStates().catch(() => ({ data: {} }))
    ])
    const [addressDictionary = {}] = businessAddressData.payload?.direcciones?.direccion || []
    const regionId = parseInt(addressDictionary.region, 10)

    const foundState = businessStatesData.payload?.find(state => state.id === regionId)

    const state = foundState ? { id: foundState.id, name: foundState.nombre } : { id: regionId, name: null }

    const { data: businessDistrictsData } = await getDistricts({ stateID: regionId }).catch(() => ({ data: {} }))

    const districtId = parseInt(addressDictionary.comuna, 10)

    const foundDistrict = businessDistrictsData.payload?.find(district => district.idSii === districtId)

    const district = foundDistrict
      ? { id: foundDistrict.id, name: foundDistrict.nombre }
      : { id: districtId, name: null }

    return batch(() => {
      dispatch(
        onGetGuestBusinessOptionsSuccess({
          address: [
            addressDictionary.calle && addressDictionary.calle !== 'No hay Dato' && addressDictionary.calle,
            addressDictionary.numero && addressDictionary.numero !== 'No hay Dato' && ` ${addressDictionary.numero}`,
            addressDictionary.departamento &&
              addressDictionary.departamento !== 'No hay Dato' &&
              `, departamento ${addressDictionary.departamento}`,
            addressDictionary.villapoblacion &&
              addressDictionary.villapoblacion !== 'No hay Dato' &&
              `, ${addressDictionary.villapoblacion}`
          ]
            .filter(Boolean)
            .join('')
            .toUpperCase(),
          district,
          incomeBracket: businessIncomeData.payload?.tramo,
          incomeBrackets: businessIncomeBracketsData.payload?.map(income => ({
            name: income.nomTramo,
            value: income.id
          })),
          incomeYear: businessIncomeYear,
          size: businessIncomeData.payload?.tamano,
          state,
          type: business.entityType === 'natural' ? 21 : 22
        })
      )
    })
  } catch (error) {
    return dispatch(onGetGuestBusinessOptionsError(error))
  }
}

export const CLEAN_GUEST_BUSINESS_SUCCESS = 'CLEAN_GUEST_BUSINESS_SUCCESS'
export const onCleanGuestBusinessSuccess = makeActionCreator(CLEAN_GUEST_BUSINESS_SUCCESS)
export const onCleanGuestBusinessThunk = payload => dispatch =>
  batch(() => {
    dispatch(onCleanGuestBusinessSuccess())
    payload.callback()
  })

export const CREATE_TEMPORARY_BUYER = 'CREATE_TEMPORARY_BUYER'
export const CREATE_TEMPORARY_BUYER_ERROR = 'CREATE_TEMPORARY_BUYER_ERROR'
export const CREATE_TEMPORARY_BUYER_SUCCESS = 'CREATE_TEMPORARY_BUYER_SUCCESS'
export const onCreateTemporaryBuyer = makeActionCreator(CREATE_TEMPORARY_BUYER)
export const onCreateTemporaryBuyerError = makeActionCreator(CREATE_TEMPORARY_BUYER_ERROR, 'payload')
export const onCreateTemporaryBuyerSuccess = makeActionCreator(CREATE_TEMPORARY_BUYER_SUCCESS, 'payload')
export const onCreateTemporaryBuyerThunk = payload => (dispatch, getState) => {
  dispatch(onCreateTemporaryBuyer())

  try {
    const { business } = getState().registerWizard

    const newBuyerData = {
      address: payload.address,
      district: payload.district,
      dni: business.dni || payload.dni,
      email: payload.email,
      name: business.name || payload.name,
      nickname: payload.nickname,
      phone: payload.phone,
      state: payload.state,
      type: business.type || payload.type,
      website: payload.website
    }

    dispatch(onCreateTemporaryBuyerSuccess(newBuyerData))

    payload.callback()
  } catch (error) {
    return dispatch(onCreateTemporaryBuyerError(error.toObject()))
  }
}

export const RESET_SEND_REGISTRATION_EMAIL = 'RESET_SEND_REGISTRATION_EMAIL'
export const onResetSendRegistrationEmail = makeActionCreator(RESET_SEND_REGISTRATION_EMAIL)

export const SET_FOREIGN_REGISTRATION = 'SET_FOREIGN_REGISTRATION'
export const onSetForeignRegistration = makeActionCreator(SET_FOREIGN_REGISTRATION, 'payload')

export const OPEN_CONFIRM_NEW_AFFIDAVIT_DIALOG = 'OPEN_CONFIRM_NEW_AFFIDAVIT_DIALOG'
export const onOpenConfirmNewAffidavitDialog = makeActionCreator(OPEN_CONFIRM_NEW_AFFIDAVIT_DIALOG)

export const CLOSE_CONFIRM_NEW_AFFIDAVIT_DIALOG = 'CLOSE_CONFIRM_NEW_AFFIDAVIT_DIALOG'
export const onCloseConfirmNewAffidavitDialog = makeActionCreator(CLOSE_CONFIRM_NEW_AFFIDAVIT_DIALOG)

export const OPEN_MODAL = 'OPEN_MODAL'
export const CLOSE_MODAL = 'CLOSE_MODAL'

export const openModal = makeActionCreator(OPEN_MODAL, 'payload')
export const closeModal = makeActionCreator(CLOSE_MODAL, 'payload')

export const INITIATE_FLOW = 'INITIATE_FLOW'
export const INITIATE_FLOW_SUCCESS = 'INITIATE_FLOW_SUCCESS'
export const INITIATE_FLOW_ERROR = 'INITIATE_FLOW_ERROR'
export const onInitiateFlow = makeActionCreator(INITIATE_FLOW)
export const onInitiateFlowSuccess = makeActionCreator(INITIATE_FLOW_SUCCESS, 'payload')
export const onInitiateFlowError = makeActionCreator(INITIATE_FLOW_ERROR, 'payload')
export const initiateFlowThunk = token => async dispatch => {
  dispatch(onInitiateFlow())

  const flow = 'INICIO_PASO_1'
  try {
    const response = await initiateFlow({ token })
    const { payload } = response.data
    return batch(() => {
      dispatch(onInitiateFlowSuccess({ flowId: payload, tokenRecaptcha: token }))
      dispatch(registerFlowThunk({ flow }))
    })
  } catch (error) {
    try {
      dispatch(
        onRegisterErrorThunk({
          origin: flow,
          message: 'Error al iniciar el flujo de registro' || error.message
        })
      )
    } catch (error) {
      throw new Error('Error al registrar el error:', registerError)
    }

    dispatch(onInitiateFlowError({ reason: error.message }))
  }
}

export const REGISTER_FLOW = 'REGISTER_FLOW'
export const REGISTER_FLOW_SUCCESS = 'REGISTER_FLOW_SUCCESS'
export const REGISTER_FLOW_ERROR = 'REGISTER_FLOW_ERROR'
export const onRegisterFlow = makeActionCreator(REGISTER_FLOW)
export const onRegisterFlowSuccess = makeActionCreator(REGISTER_FLOW_SUCCESS, 'payload')
export const onRegisterFlowError = makeActionCreator(REGISTER_FLOW_ERROR, 'payload')
export const registerFlowThunk =
  ({ flow }) =>
  async (dispatch, getState) => {
    dispatch(onRegisterFlow())
    const flowId = getState().registerWizard.flow.data?.flowId
    try {
      await registerFlow({ flowId, flow })
      dispatch(onRegisterFlowSuccess({ flowId, flow }))
    } catch (error) {
      try {
        dispatch(
          onRegisterErrorThunk({
            origin: flow,
            message: 'Error al registrar el flujo, intenta nuevamente' || error.message
          })
        )
      } catch (registerError) {
        throw new Error('Error al registrar el error:', registerError)
      }
      dispatch(onRegisterFlowError({ reason: error.message }))
    }
  }

export const SEND_VALIDATION_EMAIL = 'SEND_VALIDATION_EMAIL'
export const SEND_VALIDATION_EMAIL_SUCCESS = 'SEND_VALIDATION_EMAIL_SUCCESS'
export const SEND_VALIDATION_EMAIL_ERROR = 'SEND_VALIDATION_EMAIL_ERROR'
export const onSendValidationEmail = makeActionCreator(SEND_VALIDATION_EMAIL)
export const onSendValidationEmailSuccess = makeActionCreator(SEND_VALIDATION_EMAIL_SUCCESS, 'payload')
export const onSendValidationEmailError = makeActionCreator(SEND_VALIDATION_EMAIL_ERROR, 'payload')
export const sendValidationEmailThunk = payload => async (dispatch, getState) => {
  dispatch(onSendValidationEmail())

  const flowId = getState().registerWizard.flow?.data?.flowId

  try {
    const data = {
      dni: payload.dni || '17157036-0',
      email: payload.email,
      name: payload.name,
      motherLastName: payload.motherLastName,
      fatherLastName: payload.fatherLastName,
      flowId
    }

    await sendValidationEmail(data)

    return batch(() => {
      dispatch(onSendValidationEmailSuccess(data))
      payload.callback()
    })
  } catch (error) {
    return batch(() => {
      dispatch(onSendValidationEmailError(customizeError({ code: 500, reason: 'SOMETHING_WENT_WRONG_ERROR' })))
      dispatch(
        onRegisterErrorThunk({
          origin: 'INICIO_PASO_2',
          message: 'Error al enviar el correo de validación'
        })
      )
      if (!isLocalEnv) {
        dispatch(onLogoutUniquePassThunk({ refreshToken: payload.refreshToken }))
      }
    })
  }
}

export const REGISTER_ERROR = 'REGISTER_ERROR'
export const REGISTER_ERROR_SUCCESS = 'REGISTER_ERROR_SUCCESS'
export const REGISTER_ERROR_FAILURE = 'REGISTER_ERROR_FAILURE'
export const onRegisterError = makeActionCreator(REGISTER_ERROR)
export const onRegisterErrorSuccess = makeActionCreator(REGISTER_ERROR_SUCCESS, 'payload')
export const onRegisterErrorFailure = makeActionCreator(REGISTER_ERROR_FAILURE, 'payload')
export const onRegisterErrorThunk =
  ({ origin, message }) =>
  async (dispatch, getState) => {
    dispatch(onRegisterError())

    let flowId = getState().registerWizard.flow?.data?.flowId

    if (!flowId) {
      flowId = 'No llegó a generarse un id de flujo'
    }

    const payload = {
      flowId,
      origin,
      message,
      type: 'SERVICE_ERROR'
    }

    try {
      await registerError(payload)
      dispatch(onRegisterErrorSuccess(payload))
    } catch (error) {
      dispatch(onRegisterErrorFailure({ reason: error.message }))
    }
  }

export const UPDATE_REGISTER_WIZARD_WITH_BACKUP = 'UPDATE_REGISTER_WIZARD_WITH_BACKUP'

export const updateRegisterWizardWithBackup = backup => ({
  type: UPDATE_REGISTER_WIZARD_WITH_BACKUP,
  payload: backup
})

export const GET_REPRESENTATIVE_AUTHORITY = 'GET_REPRESENTATIVE_AUTHORITY'
export const GET_REPRESENTATIVE_AUTHORITY_SUCCESS = 'GET_REPRESENTATIVE_AUTHORITY_SUCCESS'
export const GET_REPRESENTATIVE_AUTHORITY_ERROR = 'GET_REPRESENTATIVE_AUTHORITY_ERROR'
export const onGetRepresentativeAuthority = makeActionCreator(GET_REPRESENTATIVE_AUTHORITY)
export const onGetRepresentativeAuthoritySuccess = makeActionCreator(GET_REPRESENTATIVE_AUTHORITY_SUCCESS, 'payload')
export const onGetRepresentativeAuthorityError = makeActionCreator(GET_REPRESENTATIVE_AUTHORITY_ERROR, 'payload')
export const getRepresentativeAuthorityThunk = payload => async dispatch => {
  dispatch(onGetRepresentativeAuthority())
  try {
    const { data } = await getIdentity({ dni: formatDNI(payload.dni, 2) })
    const { nombres: names, apellidoPaterno: fatherLastName, apellidoMaterno: motherLastName } = data.payload

    /* Validates if the user is underage */
    const dateOfBirth = parseISO(data.payload.fechaNacimiento)
    if (differenceInYears(new Date(), dateOfBirth) < 18) {
      const errorMessage = 'El usuario es menor de edad'
      return batch(() => {
        dispatch(onGetRepresentativeAuthorityError(customizeError({ code: 400, reason: 'UNDERAGE_USER_ERROR' })))
        dispatch(
          onRegisterErrorThunk({
            origin: 'INICIO_PASO_4',
            message: errorMessage
          })
        )
        if (!isLocalEnv) {
          dispatch(onLogoutUniquePassThunk({ refreshToken: payload.refreshToken }))
        }
      })
    }

    return dispatch(onGetRepresentativeAuthoritySuccess({ names, fatherLastName, motherLastName }))
  } catch (error) {
    /* Validates if the user is deceased */
    if (error.code === 400 && error.originalError?.response?.data?.errores?.[0]?.codigo === '400-6') {
      const userInfo = error.originalError.response.data.errores[0].descripcion
      return batch(() => {
        dispatch(onGetRepresentativeAuthorityError(customizeError({ code: 400, reason: 'DECEASED_PERSON_ERROR' })))
        dispatch(
          onRegisterErrorThunk({
            origin: 'INICIO_PASO_4',
            message: userInfo
          })
        )
        if (!isLocalEnv) {
          dispatch(onLogoutUniquePassThunk({ refreshToken: payload.refreshToken }))
        }
      })
    }

    return batch(() => {
      dispatch(onGetRepresentativeAuthorityError(customizeError({ code: 500, reason: 'SOMETHING_WENT_WRONG_ERROR' })))
      dispatch(
        onRegisterErrorThunk({
          origin: 'INICIO_PASO_4',
          message: 'Error de servidor. Por favor intente nuevamente más tarde.'
        })
      )
      if (!isLocalEnv) {
        dispatch(onLogoutUniquePassThunk({ refreshToken: payload.refreshToken }))
      }
    })
  }
}

export const GET_COLLABORATION_AGREEMENT_CHOICES = 'GET_COLLABORATION_AGREEMENT_CHOICES'
export const GET_COLLABORATION_AGREEMENT_CHOICES_SUCCESS = 'GET_COLLABORATION_AGREEMENT_CHOICES_SUCCESS'
export const GET_COLLABORATION_AGREEMENT_CHOICES_ERROR = 'GET_COLLABORATION_AGREEMENT_CHOICES_ERROR'
export const onGetCollaborationAgreementChoices = makeActionCreator(GET_COLLABORATION_AGREEMENT_CHOICES)
export const onGetCollaborationAgreementChoicesSuccess = makeActionCreator(
  GET_COLLABORATION_AGREEMENT_CHOICES_SUCCESS,
  'payload'
)
export const onGetCollaborationAgreementChoicesError = makeActionCreator(
  GET_COLLABORATION_AGREEMENT_CHOICES_ERROR,
  'payload'
)
export const getCollaborationAgreementChoicesThunk = payload => async dispatch => {
  dispatch(onGetCollaborationAgreementChoices())
  try {
    const { data } = await getOrganismTypes()

    return dispatch(
      onGetCollaborationAgreementChoicesSuccess({
        types: data.payload?.map(type => ({ id: type.id, name: type.nombre }))
      })
    )
  } catch (error) {
    return batch(() => {
      dispatch(
        onGetCollaborationAgreementChoicesError(customizeError({ code: 500, reason: 'SOMETHING_WENT_WRONG_ERROR' }))
      )
      dispatch(
        onRegisterErrorThunk({
          origin: 'INICIO_PASO_5',
          message: 'Error de servidor. Por favor intente nuevamente más tarde.'
        })
      )
      if (!isLocalEnv) {
        dispatch(onLogoutUniquePassThunk({ refreshToken: payload.refreshToken }))
      }
    })
  }
}

export const REGISTER_COLLABORATION_AGREEMENT_CHOICES = 'REGISTER_COLLABORATION_AGREEMENT_CHOICES'
export const REGISTER_COLLABORATION_AGREEMENT_CHOICES_SUCCESS = 'REGISTER_COLLABORATION_AGREEMENT_CHOICES_SUCCESS'
export const REGISTER_COLLABORATION_AGREEMENT_CHOICES_ERROR = 'REGISTER_COLLABORATION_AGREEMENT_CHOICES_ERROR'
export const onRegisterCollaborationAgreementChoices = makeActionCreator(REGISTER_COLLABORATION_AGREEMENT_CHOICES)
export const onRegisterCollaborationAgreementChoicesSuccess = makeActionCreator(
  REGISTER_COLLABORATION_AGREEMENT_CHOICES_SUCCESS,
  'payload'
)
export const onRegisterCollaborationAgreementChoicesError = makeActionCreator(
  REGISTER_COLLABORATION_AGREEMENT_CHOICES_ERROR,
  'payload'
)
export const registerCollaborationAgreementChoicesThunk = payload => async dispatch => {
  dispatch(onRegisterCollaborationAgreementChoices())
  try {
    const choices = {
      mandatoryAgreement: payload.firstChoiceValue === '1',
      agreementType: payload.firstOptionalValue || ''
    }
    dispatch(onRegisterCollaborationAgreementChoicesSuccess({ choices }))
  } catch (error) {
    try {
      dispatch(
        onRegisterErrorThunk({
          origin: 'INICIO_PASO_5',
          message: 'Error al registrar las opciones elegidas, intenta nuevamente'
        })
      )
    } catch (registerError) {
      throw new Error('Error al registrar el error:', registerError)
    }
    dispatch(onRegisterCollaborationAgreementChoicesError({ reason: error.message }))
  }
}

export const GET_REPRESENTATIVES = 'GET_REPRESENTATIVES'
export const GET_REPRESENTATIVES_ERROR = 'GET_REPRESENTATIVES_ERROR'
export const GET_REPRESENTATIVES_SUCCESS = 'GET_REPRESENTATIVES_SUCCESS'
export const onGetRepresentatives = makeActionCreator(GET_REPRESENTATIVES)
export const onGetRepresentativesError = makeActionCreator(GET_REPRESENTATIVES_ERROR, 'payload')
export const onGetRepresentativesSuccess = makeActionCreator(GET_REPRESENTATIVES_SUCCESS, 'payload')
export const onGetRepresentativesThunk = () => async (dispatch, getState) => {
  dispatch(onGetRepresentatives())

  try {
    const {
      signer: { dni: signerDNI },
      identifierType,
      isCivilRegistry
    } = getState().registerWizard.signerIdentification

    const { business } = getState().registerWizard
    const formattedDNI = formatDNI(business.dni, 2)
    const { data } = await getRepresentatives({ dni: formattedDNI }).catch(() => {
      throw customizeError({ code: 400, reason: 'SOMETHING_WENT_WRONG_ERROR' })
    })

    const representative = getPersonsFromServiceData(data.payload.representantes)

    return dispatch(
      onGetRepresentativesSuccess({
        identifierType: representative.find(person => person.dni === signerDNI)?.dni || identifierType,
        representative,
        isCivilRegistry
      })
    )
  } catch (error) {
    return dispatch(
      onGetRepresentativesError({
        code: error.code,
        message: error.reason
      })
    )
  }
}

export const GET_SIGNER_INFO = 'GET_SIGNER_INFO'
export const GET_SIGNER_INFO_ERROR = 'GET_SIGNER_INFO_ERROR'
export const GET_SIGNER_INFO_SUCCESS = 'GET_SIGNER_INFO_SUCCESS'
export const onGetSignerInfo = makeActionCreator(GET_SIGNER_INFO)
export const onGetSignerInfoError = makeActionCreator(GET_SIGNER_INFO_ERROR, 'payload')
export const onGetSignerInfoSuccess = makeActionCreator(GET_SIGNER_INFO_SUCCESS)
export const onGetSignerInfoThunk = payload => async (dispatch, getState) => {
  dispatch(onGetSignerInfo())
  const { representative } = getState().registerWizard.signerIdentification
  try {
    const { data } = await getIdentity({
      actionType: GET_SIGNER_INFO,
      dni: formatDNI(payload.dni, 2)
    })
    if (data?.payload?.fechaDefuncion !== '0000-00-00') {
      throw customizeError({ code: 400, reason: 'DECEASED_PERSON_ERROR' })
    }
    const signerSelectValue = {
      dni: formatDNI(payload.dni) || '',
      name: data.payload?.nombres || '',
      gender: data.payload?.sexo || '',
      lastNamePaternal: data.payload?.apellidoPaterno || '',
      lastNameMaternal: data.payload?.apellidoMaterno || ''
    }
    const identifierType =
      representative.find(item => item.dni === signerSelectValue.dni)?.dni || IDENTIFICATION_TYPE.DNI
    return batch(() => {
      dispatch(onGetSignerInfoSuccess())
      dispatch(onSetSignerThunk({ signerSelectValue, identifierType }))
    })
  } catch (error) {
    let reason
    switch (error.code) {
      case 400:
        reason = error.reason
        break
      case 404:
      case 422:
        reason = 'DNI_DOES_NOT_MATCH_ERROR'
        break
      default:
        reason = 'SOMETHING_WENT_WRONG_ERROR'
        break
    }

    return batch(() => {
      dispatch(
        onGetSignerInfoError({
          error: customizeError({ reason, code: error.code, originalError: error?.originalError }).toObject(),
          dni: formatDNI(payload.dni) || ''
        })
      )

      // If we get an error with the service, we must allow to sign the affidavit
      const signer = representative.find(item => item.dni === formatDNI(payload.dni))
      if (signer && reason === 'SOMETHING_WENT_WRONG_ERROR') {
        dispatch(
          onSetSignerThunk({
            signerSelectValue: {
              dni: formatDNI(payload.dni) || '',
              name: signer?.name.replace('* ', '') || '',
              lastNamePaternal: signer?.lastNamePaternal || '',
              lastNameMaternal: signer?.lastNameMaternal || ''
            },
            identifierType: signer?.dni
          })
        )
      }
    })
  }
}

export const SET_SIGNER_ERROR = 'SET_SIGNER_ERROR'
export const SET_SIGNER_SUCCESS = 'SET_SIGNER_SUCCESS'
export const onSetSignerError = makeActionCreator(SET_SIGNER_ERROR, 'payload')
export const onSetSignerSuccess = makeActionCreator(SET_SIGNER_SUCCESS, 'payload')
export const onSetSignerThunk = payload => async dispatch => {
  try {
    if (payload && (payload.signerSelectValue?.dni || payload.dni)) {
      let lastName = ''

      if (![null, '', undefined].includes(payload.signerSelectValue?.lastNamePaternal)) {
        lastName += payload.signerSelectValue?.lastNamePaternal
      }

      if (![null, '', undefined].includes(payload.signerSelectValue?.lastNameMaternal)) {
        lastName += ![null, '', undefined].includes(payload.signerSelectValue?.lastNamePaternal)
          ? ` ${payload.signerSelectValue?.lastNameMaternal}`
          : payload.signerSelectValue?.lastNameMaternal
      }

      return dispatch(
        onSetSignerSuccess({
          signer: {
            dni: payload.signerSelectValue?.dni || payload.dni,
            fullName: createNameComplete(payload),
            name: payload.signerSelectValue?.name,
            lastName,
            lastNamePaternal: payload.signerSelectValue?.lastNamePaternal,
            lastNameMaternal: payload.signerSelectValue?.lastNameMaternal,
            gender: payload.signerSelectValue?.gender
          },
          identifierType: payload?.identifierType,
          isCivilRegistry: true
        })
      )
    }
  } catch (error) {
    return dispatch(onSetSignerError({ error: { code: error.code, message: error.reason } }))
  }
}

export const SET_HAS_ERROR = 'SET_HAS_ERROR'
export const onSetHasError = makeActionCreator(SET_HAS_ERROR, 'payload')

export const SET_SIGNER_NAME_SUCCESS = 'SET_SIGNER_NAME_SUCCESS'
export const SET_SIGNER_NAME_ERROR = 'SET_SIGNER_NAME_ERROR'
export const onSetSignerNameSuccess = makeActionCreator(SET_SIGNER_NAME_SUCCESS, 'payload')
export const onSetSignerNameError = makeActionCreator(SET_SIGNER_NAME_ERROR, 'payload')
export const onSetSignerNameThunk = signer => async dispatch => {
  try {
    return dispatch(onSetSignerNameSuccess({ signer }))
  } catch (error) {
    return dispatch(onSetSignerNameError({ code: 500, reason: 'ERROR_SETTING_SIGNER_NAME' }))
  }
}

export const SEND_RECEIPT_EMAIL = 'SEND_RECEIPT_EMAIL'
export const SEND_RECEIPT_EMAIL_SUCCESS = 'SEND_RECEIPT_EMAIL_SUCCESS'
export const SEND_RECEIPT_EMAIL_ERROR = 'SEND_RECEIPT_EMAIL_ERROR'

export const onSendReceiptEmail = makeActionCreator(SEND_RECEIPT_EMAIL)
export const onSendReceiptEmailSuccess = makeActionCreator(SEND_RECEIPT_EMAIL_SUCCESS, 'payload')
export const onSendReceiptEmailError = makeActionCreator(SEND_RECEIPT_EMAIL_ERROR, 'payload')

export const sendReceiptEmailThunk = () => async dispatch => {
  dispatch(onSendReceiptEmail())

  return batch(() => {
    dispatch(onSendReceiptEmailSuccess({ success: { code: 200, message: 'success' } }))
  })
}

export const GET_DIRECTOR = 'GET_DIRECTOR'
export const GET_DIRECTOR_SUCCESS = 'GET_DIRECTOR_SUCCESS'
export const GET_DIRECTOR_ERROR = 'GET_DIRECTOR_ERROR'
export const onGetDirector = makeActionCreator(GET_DIRECTOR)
export const onGetDirectorSuccess = makeActionCreator(GET_DIRECTOR_SUCCESS, 'payload')
export const onGetDirectorError = makeActionCreator(GET_DIRECTOR_ERROR, 'payload')
export const onGetDirectorThunk = payload => async dispatch => {
  dispatch(onGetDirector())

  try {
    const { data } = await getDirectors()
    return dispatch(
      onGetDirectorSuccess({
        types: {
          id: data.payload.id,
          dni: data.payload.rut,
          firstName: data.payload.nombres,
          lastName: data.payload.apellidos,
          gender: data.payload.genero,
          dateFrom: data.payload.fechaDesde,
          dateUntil: data.payload.fechaHasta,
          order: data.payload.orden,
          resolutionDecreeAppointment: data.payload.resolucionDecretoNombramiento,
          dateResolutionDecreeAppointment: data.payload.fechaResolucionDecretoNombramiento
        }
      })
    )
  } catch (error) {
    return batch(() => {
      dispatch(onGetDirectorError(customizeError({ code: 500, reason: 'SOMETHING_WENT_WRONG_ERROR' })))
      if (error.originalError?.response?.data?.errores?.[0]?.codigo === '400-1') {
        dispatch(onGetDirectorError(customizeError({ code: 500, reason: 'WHITHOUT_DIRECTOR_ERROR' })))
      } else {
        dispatch(onGetDirectorError(customizeError({ code: 500, reason: 'SOMETHING_WENT_WRONG_ERROR' })))
        dispatch(
          onRegisterErrorThunk({
            origin: 'INICIO_PASO_5_BIS',
            message: 'Error de servidor. Por favor intente nuevamente más tarde.'
          })
        )
      }
      if (!isLocalEnv) {
        dispatch(onLogoutUniquePassThunk({ refreshToken: payload.refreshToken }))
      }
    })
  }
}

export const GET_SURROGANT = 'GET_SURROGANT'
export const GET_SURROGANT_SUCCESS = 'GET_SURROGANT_SUCCESS'
export const GET_SURROGANT_ERROR = 'GET_SURROGANT_ERROR'
export const onGetSurrogant = makeActionCreator(GET_SURROGANT)
export const onGetSurrogantSuccess = makeActionCreator(GET_SURROGANT_SUCCESS, 'payload')
export const onGetSurrogantError = makeActionCreator(GET_SURROGANT_ERROR, 'payload')
export const onGetSurrogantThunk = payload => async dispatch => {
  dispatch(onGetSurrogant())

  try {
    const { data } = await getSurrogants()

    return dispatch(
      onGetSurrogantSuccess({
        types: data.payload?.map(type => ({
          id: type.id,
          dni: type.rut,
          firstName: type.nombres,
          lastName: type.apellidos,
          gender: type.genero,
          dateFrom: type.fechaDesde,
          dateUntil: type.fechaHasta,
          order: type.orden,
          dateResolutionDecreeAppointment: type.fechaResolucionDecretoNombramiento,
          resolutionDecreeAppointment: type.resolucionDecretoNombramiento
        }))
      })
    )
  } catch (error) {
    return batch(() => {
      dispatch(onGetSurrogantError(customizeError({ code: 500, reason: 'SOMETHING_WENT_WRONG_ERROR' })))
      dispatch(
        onRegisterErrorThunk({
          origin: 'INICIO_PASO_5_BIS',
          message: 'Error de servidor. Por favor intente nuevamente más tarde.'
        })
      )
      if (!isLocalEnv) {
        dispatch(onLogoutUniquePassThunk({ refreshToken: payload.refreshToken }))
      }
    })
  }
}

export const REGISTER_COLLABORATION_INFO = 'REGISTER_COLLABORATION_INFO'
export const REGISTER_COLLABORATION_INFO_SUCCESS = 'REGISTER_COLLABORATION_INFO_SUCCESS'
export const REGISTER_COLLABORATION_INFO_ERROR = 'REGISTER_COLLABORATION_INFO_ERROR'
export const onRegisterCollaborationInfo = makeActionCreator(REGISTER_COLLABORATION_INFO)
export const onRegisterCollaborationInfoSuccess = makeActionCreator(REGISTER_COLLABORATION_INFO_SUCCESS, 'payload')
export const onRegisterCollaborationInfoError = makeActionCreator(REGISTER_COLLABORATION_INFO_ERROR, 'payload')

export const GET_DOWNLOAD_FILE_URL = 'GET_DOWNLOAD_FILE'
export const GET_DOWNLOAD_FILE_URL_SUCCESS = 'GET_DOWNLOAD_FILE_SUCCESS'
export const GET_DOWNLOAD_FILE_URL_ERROR = 'GET_DOWNLOAD_FILE_ERROR'
export const onGetDownloadFileUrl = makeActionCreator(GET_DOWNLOAD_FILE_URL)
export const onGetDownloadFileUrlSuccess = makeActionCreator(GET_DOWNLOAD_FILE_URL_SUCCESS, 'payload')
export const onGetDownloadFileUrlError = makeActionCreator(GET_DOWNLOAD_FILE_URL_ERROR, 'payload')
export const onGetDownloadFileThunk = payload => async dispatch => {
  dispatch(onGetDownloadFileUrl())

  const maxRetries = 3
  const delay = 3000

  const retryFetch = async retries => {
    try {
      const { data } = await getFileFromBucket({ iddoc: payload.iddoc })

      if (data && data.URL) {
        return dispatch(
          onGetDownloadFileUrlSuccess({
            fileUrl: data.URL
          })
        )
      }
    } catch (error) {
      if (error.code === 400 && retries > 0) {
        await new Promise(resolve => setTimeout(resolve, delay))

        return retryFetch(retries - 1)
      } else {
        return batch(() => {
          dispatch(onGetDownloadFileUrlError(customizeError({ code: 500, reason: 'SOMETHING_WENT_WRONG_ERROR' })))
          dispatch(
            onRegisterErrorThunk({
              origin: 'INICIO_PASO_4',
              message: 'Error de servidor. Por favor intente nuevamente más tarde.'
            })
          )
          if (!isLocalEnv) {
            dispatch(onLogoutUniquePassThunk({ refreshToken: payload.refreshToken }))
          }
        })
      }
    }
  }
  return retryFetch(maxRetries)
}

export const REGISTER_BUYER = 'REGISTER_BUYER'
export const REGISTER_BUYER_SUCCESS = 'REGISTER_BUYER_SUCCESS'
export const REGISTER_BUYER_ERROR = 'REGISTER_BUYER_ERROR'
export const onRegisterBuyer = makeActionCreator(REGISTER_BUYER)
export const onRegisterBuyerSuccess = makeActionCreator(REGISTER_BUYER_SUCCESS, 'payload')
export const onRegisterBuyerError = makeActionCreator(REGISTER_BUYER_ERROR, 'payload')
export const registerBuyerThunk =
  ({ executeRecaptcha = () => undefined, onSuccessCallback = undefined } = {}) =>
  async (dispatch, getState) => {
    dispatch(onRegisterBuyer())

    const state = getState().registerWizard
    let token = '1234'
    if (REACT_APP_FEATURE_FLAG_RECAPTCHA_ENABLED === true || REACT_APP_FEATURE_FLAG_RECAPTCHA_ENABLED === 'true') {
      token = await executeRecaptcha()
    }

    try {
      const requestData = {
        institucion: renameProperties(state.business, {
          address: 'domicilioLegal',
          entityType: 'actividad',
          name: 'razonSocial',
          nickname: 'nombreFantasia',
          state: 'region',
          district: 'comuna',
          dni: 'rut',
          type: 'tipoInstitucion',
          email: 'correo',
          phone: 'telefono',
          website: 'sitioWeb'
        }),
        director: !state?.director?.firstName
          ? null
          : renameProperties(state.director, {
              dni: 'rut',
              firstName: 'nombres',
              lastName: 'apellidos',
              dateFrom: 'fechaDesde',
              dateUntil: 'fechaHasta',
              gender: 'genero',
              order: 'orden',
              dateResolutionDecreeAppointment: 'fechaResolucionDecretoNombramiento',
              resolutionDecreeAppointment: 'resolucionDecretoNombramiento'
            }),
        mandatoryAgreement: state.collaborationAgreement.mandatoryAgreement,
        agreementType: [null, '', undefined].includes(state.collaborationAgreement.agreementType)
          ? null
          : {
              id: state.collaborationAgreement.agreementType
            },
        adhesionAgreement: state.collaborationAgreement.mandatoryAgreement
          ? null
          : {
              signer: renameProperties(state.signerIdentification.signer, {
                dni: 'rut',
                name: 'nombres',
                lastName: 'apellidos'
              }),
              textoConvenio: state.documentValue,
              validadoEnRegistroCivil: !!state.signerIdentification.isCivilRegistry // me falta anexar un campo para esto
            },
        emailVerify: state.emailVerify.code,
        flow: { idFlujo: state.flow.data.flowId },
        guest: renameProperties(state.guest, {
          isUniquePass: 'esClaveUnica',
          name: 'nombres',
          lastName: 'apellidos',
          dni: 'rut',
          gender: 'genero',
          phone: 'telefono',
          position: 'cargo',
          email: 'correo'
        }),
        representativeAuthority: renameProperties(state.representativeAuthority, {
          dni: 'rut',
          name: 'nombres',
          lastName: 'apellidos',
          files: 'archivos',
          phone: 'telefono',
          position: 'cargo',
          email: 'correo',
          isUniquePass: 'esClaveUnica'
        }),
        surrogantsList: state.surrogantsList.map(surrogant =>
          renameProperties(surrogant, {
            dni: 'rut',
            firstName: 'nombres',
            lastName: 'apellidos',
            dateFrom: 'fechaDesde',
            dateUntil: 'fechaHasta',
            gender: 'genero',
            order: 'orden',
            dateResolutionDecreeAppointment: 'fechaResolucionDecretoNombramiento',
            resolutionDecreeAppointment: 'resolucionDecretoNombramiento'
          })
        ),
        legalRepresentation: state.legalRepresentation,
        token
      }

      const { data } = await sendBuyerRegistrationData(requestData)

      return batch(() => {
        dispatch(onRegisterBuyerSuccess(data))
        onSuccessCallback()
      })
    } catch (error) {
      try {
        dispatch(
          onRegisterErrorThunk({
            origin: 'INICIO_PASO_5',
            message: 'Error al registrar al comprador, intenta nuevamente'
          })
        )
      } catch (registerError) {
        throw new Error('Error al registrar el error:', registerError)
      }

      dispatch(onRegisterBuyerError({ reason: error.message }))
    }
  }

export const DONWLOAD_PDF = 'DONWLOAD_PDF'
export const DONWLOAD_PDF_SUCCESS = 'DONWLOAD_PDF_SUCCESS'
export const DONWLOAD_PDF_ERROR = 'DONWLOAD_PDF_ERROR'
export const onDownloadPDF = makeActionCreator(DONWLOAD_PDF)
export const onDownloadPDFSuccess = makeActionCreator(DONWLOAD_PDF_SUCCESS, 'payload')
export const onDownloadPDFError = makeActionCreator(DONWLOAD_PDF_ERROR, 'payload')
export const onDownloadPDFThunk =
  ({ inscriptionId }) =>
  async dispatch => {
    dispatch(onDownloadPDF())
    try {
      const response = await getBuyerRegistrationPDF({ numeroSolicitud: inscriptionId })
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'comprobante_solicitud.pdf')
      document.body.appendChild(link)
      link.click()
      link.parentNode.removeChild(link)

      return dispatch(onDownloadPDFSuccess(url))
    } catch (error) {
      return batch(() => {
        dispatch(onDownloadPDFError(customizeError({ code: 500, reason: error.reason })))
        dispatch(
          onRegisterErrorThunk({
            origin: 'INICIO_PASO_6',
            message: 'Error al descargar el PDF.'
          })
        )
      })
    }
  }

export const SEND_PDF_EMAIL = 'SEND_PDF_EMAIL'
export const SEND_PDF_EMAIL_SUCCESS = 'SEND_PDF_EMAIL_SUCCESS'
export const SEND_PDF_EMAIL_ERROR = 'SEND_PDF_EMAIL_ERROR'
export const onSendPdfEmail = makeActionCreator(SEND_PDF_EMAIL)
export const onSendPdfEmailSuccess = makeActionCreator(SEND_PDF_EMAIL_SUCCESS, 'payload')
export const onSendPdfEmailError = makeActionCreator(SEND_PDF_EMAIL_ERROR, 'payload')
export const onSendPdfEmailThunk = payload => async dispatch => {
  dispatch(onSendPdfEmail())

  try {
    const data = {
      email: payload.email,
      requestCode: payload.requestCode
    }

    await sendBuyerRegistrationEmail(data)

    return batch(() => {
      dispatch(onSendPdfEmailSuccess(data))
    })
  } catch (error) {
    return batch(() => {
      dispatch(onSendPdfEmailError(customizeError({ code: 500, reason: 'SOMETHING_WENT_WRONG_ERROR' })))
      dispatch(
        onRegisterErrorThunk({
          origin: 'INICIO_PASO_6',
          message: 'Error al enviar el correo con los datos de registro'
        })
      )
    })
  }
}

export const SET_DOCUMENT_VALUE = 'SET_DOCUMENT_VALUE'
export const SET_DOCUMENT_VALUE_SUCCESS = 'SET_DOCUMENT_VALUE_SUCCESS'
export const SET_DOCUMENT_VALUE_ERROR = 'SET_DOCUMENT_VALUE_ERROR'
export const onSetDocumentValue = makeActionCreator(SET_DOCUMENT_VALUE)
export const onSetDocumentValueSuccess = makeActionCreator(SET_DOCUMENT_VALUE_SUCCESS, 'payload')
export const onSetDocumentValueError = makeActionCreator(SET_DOCUMENT_VALUE_ERROR, 'payload')
export const onSetDocumentValueThunk = value => async dispatch => {
  dispatch(onSetDocumentValue())

  try {
    const documentData = { documentValue: value }

    return dispatch(onSetDocumentValueSuccess(documentData))
  } catch (error) {
    return dispatch(onSetDocumentValueError({ code: 500, reason: 'ERROR_SETTING_DOCUMENT_VALUE' }))
  }
}

export const DOWNLOAD_AGREEMENT_COLLABORATION_PDF = 'DOWNLOAD_COLLABORATION_AGREEMENT_PDF'
export const DOWNLOAD_AGREEMENT_COLLABORATION_PDF_SUCCESS = 'DOWNLOAD_COLLABORATION_AGREEMENT_PDF_SUCCESS'
export const DOWNLOAD_AGREEMENT_COLLABORATION_PDF_ERROR = 'DOWNLOAD_COLLABORATION_AGREEMENT_PDF_ERROR'
export const onDonwloadAgreementCollaborationPdf = makeActionCreator(DOWNLOAD_AGREEMENT_COLLABORATION_PDF)
export const onDonwloadAgreementCollaborationSuccess = makeActionCreator(
  DOWNLOAD_AGREEMENT_COLLABORATION_PDF_SUCCESS,
  'payload'
)
export const onDonwloadAgreementCollaborationError = makeActionCreator(
  DOWNLOAD_AGREEMENT_COLLABORATION_PDF_ERROR,
  'payload'
)
export const onDownloadAgreementCollaborationPdfThunk =
  ({ agreementText, business, signer }) =>
  async dispatch => {
    dispatch(onDonwloadAgreementCollaborationPdf())

    try {
      const data = {
        convenio: {
          textoConvenio: agreementText
        },
        institucion: {
          rut: business.dni,
          razonSocial: business.name,
          direccion: business.address
        },
        firmante: {
          rut: signer.dni,
          nombres: signer.name,
          apellidos: signer.lastName
        }
      }

      const response = await getBuyerAgreementPDF(data)

      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'convenio_adhesion.pdf')
      document.body.appendChild(link)
      link.click()
      link.parentNode.removeChild(link)

      return dispatch(onDonwloadAgreementCollaborationSuccess())
    } catch (error) {
      return dispatch(onDonwloadAgreementCollaborationError({ code: 500, reason: 'ERROR_GETTING_DOCUMENT' }))
    }
  }
